import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Select, Space, Tag, Popconfirm, message, Typography } from 'antd';
import { PlusOutlined, SendOutlined, DeleteOutlined, BarChartOutlined } from '@ant-design/icons';
import { embotAPI } from '../../services/api';

const { Text } = Typography;
const { TextArea } = Input;

const STATUS_COLORS = { draft: 'blue', sending: 'orange', sent: 'green', failed: 'red' };

export default function EMCampaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [statsModal, setStatsModal] = useState(null);
  const [form] = Form.useForm();

  const fetch = async () => {
    setLoading(true);
    try {
      const res = await embotAPI.listCampaigns();
      setCampaigns(res.data || []);
    } catch { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { fetch(); }, []);

  const handleCreate = async () => {
    try {
      const values = await form.validateFields();
      await embotAPI.createCampaign(values);
      message.success('Campaign created');
      setModalOpen(false);
      form.resetFields();
      fetch();
    } catch (e) {
      if (e.message) message.error(e.message);
    }
  };

  const handleSend = async (id) => {
    try {
      const res = await embotAPI.sendCampaign(id);
      const d = res.data;
      if (d.ok) {
        message.success(`Sent! ${d.sent}/${d.total} delivered`);
      } else {
        message.error(d.error || 'Send failed');
      }
      fetch();
    } catch (e) {
      message.error(e.message);
    }
  };

  const handleDelete = async (id) => {
    await embotAPI.deleteCampaign(id);
    message.success('Deleted');
    fetch();
  };

  const showStats = async (id) => {
    try {
      const res = await embotAPI.campaignStats(id);
      setStatsModal(res.data);
    } catch { message.error('Failed to load stats'); }
  };

  const columns = [
    { title: 'Name', dataIndex: 'name', width: 200, ellipsis: true },
    {
      title: 'Status', dataIndex: 'status', width: 90,
      render: s => <Tag color={STATUS_COLORS[s] || 'default'}>{s}</Tag>,
    },
    { title: 'Subject', dataIndex: 'subject', width: 250, ellipsis: true },
    {
      title: 'Tags', dataIndex: 'target_tags', width: 150,
      render: tags => (tags || []).map(t => <Tag key={t}>{t}</Tag>),
    },
    { title: 'Sent', dataIndex: 'sent_count', width: 70, align: 'center' },
    { title: 'Bounce', dataIndex: 'bounce_count', width: 70, align: 'center' },
    {
      title: 'Date', dataIndex: 'sent_at', width: 150,
      render: v => v ? new Date(v).toLocaleString('zh-TW') : '-',
    },
    {
      title: 'Actions', width: 160, fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          {record.status === 'draft' && (
            <Popconfirm title="Send this campaign?" onConfirm={() => handleSend(record.id)}>
              <Button size="small" type="primary" icon={<SendOutlined />}>Send</Button>
            </Popconfirm>
          )}
          <Button size="small" icon={<BarChartOutlined />} onClick={() => showStats(record.id)} />
          <Popconfirm title="Delete?" onConfirm={() => handleDelete(record.id)}>
            <Button size="small" icon={<DeleteOutlined />} danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => { form.resetFields(); setModalOpen(true); }}>
          New Campaign
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={campaigns}
        rowKey="id"
        loading={loading}
        size="middle"
        pagination={{ pageSize: 20 }}
        scroll={{ x: 1100 }}
      />

      <Modal
        title="New Campaign"
        open={modalOpen}
        onOk={handleCreate}
        onCancel={() => setModalOpen(false)}
        width={640}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="Campaign Name" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="from_addr" label="From" initialValue="jeff@comboware.tw">
            <Select options={[
              { value: 'jeff@comboware.tw', label: 'jeff@comboware.tw' },
              { value: 'admin@comboware.tw', label: 'admin@comboware.tw' },
            ]} />
          </Form.Item>
          <Form.Item name="subject" label="Subject" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="body_html" label="Email Body (HTML)" rules={[{ required: true }]}>
            <TextArea rows={8} placeholder="<h1>Hello {{name}}</h1><p>Your content here...</p>" />
          </Form.Item>
          <Form.Item name="target_tags" label="Target Tags (send to contacts with these tags)">
            <Select mode="tags" placeholder="Leave empty to send to all contacts" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Campaign Stats"
        open={!!statsModal}
        onCancel={() => setStatsModal(null)}
        footer={null}
        width={600}
      >
        {statsModal && (
          <div>
            <Space size="large" style={{ marginBottom: 16 }}>
              <Tag color="green">Sent: {statsModal.sent}</Tag>
              <Tag color="red">Failed: {statsModal.failed}</Tag>
              <Tag color="blue">Opened: {statsModal.opened}</Tag>
            </Space>
            <Table
              size="small"
              dataSource={statsModal.logs || []}
              rowKey="id"
              pagination={{ pageSize: 10 }}
              columns={[
                { title: 'To', dataIndex: 'to_addr', ellipsis: true },
                { title: 'Status', dataIndex: 'status', width: 80, render: s => <Tag color={s === 'sent' ? 'green' : 'red'}>{s}</Tag> },
                { title: 'Sent At', dataIndex: 'sent_at', width: 160, render: v => v ? new Date(v).toLocaleString('zh-TW') : '-' },
                { title: 'Error', dataIndex: 'error', ellipsis: true },
              ]}
            />
          </div>
        )}
      </Modal>
    </div>
  );
}
